import { getConsumerId, getId } from "@carsdotcom/ccdp-sdk";
import axios from "axios";
import { Dispatch } from "redux";
import {
    getTranscriptFailure,
    getTranscriptRequest,
    getTranscriptSuccess,
    postConversationMessageFailure,
    postConversationMessageRequest,
    postConversationMessageSuccess,
    postConversationStartRequest,
    postStartConversationFailure,
    postStartConversationSuccess,
} from "src/components/Chat/redux/actions";
import { getSerializedInitialChat } from "src/components/Chat/redux/selectors";
import getConversationsEmbedConfig from "src/components/Chat/utils/getConversationsEmbedConfig";
import { Store } from "src/store";
import { ActionType } from "src/store/dataService/actions/utils/actionCreator";
import { findRoute } from "src/store/dataService/routes/util";
import { MessageType } from "Types/api/Messages";
import { v4 as uuidv4 } from "uuid";

const postChat = async (
    next: Dispatch,
    {
        payload: {
            message: { text, tempId },
        },
    }: ActionType<typeof postConversationMessageRequest>,
    store: Store
) => {
    try {
        const state = store.getState();

        const config = getConversationsEmbedConfig();

        if (state.messages.isLeavingMessage) {
            // Need to get the date time before making the request because this will trigger conversation-end,
            // and we want our message to appear first
            const displayDate = new Date().toUTCString();

            await axios.post(
                `${config.endpoint}/conversation/visitor/leavemessage`,
                {
                    channelName: state.messages.conversations_config?.channel,
                    message: text,
                    first_name: state.deal.person?.first_name,
                    last_name: state.deal.person?.last_name,
                    phone_mobile: state.deal.person?.mobile_phone,
                    email: state.deal.person?.email,
                    team_id: state.messages.selectedTeam,
                },
                {
                    headers: {
                        "x-visitor-token": state.messages.conversations_config?.token,
                    },
                }
            );

            next(
                postConversationMessageSuccess(tempId, {
                    id: uuidv4(),
                    displayDate: displayDate,
                    message: text,
                    type: MessageType.MessageLeft,
                })
            );
        } else {
            const response = await axios.post(
                `${config.endpoint}/n/bot/visitor/message`,
                {
                    channel: state.messages.conversations_config?.channel,
                    messageText: text,
                },
                {
                    headers: {
                        "x-visitor-token": state.messages.conversations_config?.token,
                    },
                }
            );

            next(
                postConversationMessageSuccess(tempId, {
                    id: response.data.id,
                    displayDate: response.data.timestamp,
                    message: text,
                    type: MessageType.Visitor,
                })
            );
        }
    } catch (e) {
        next(postConversationMessageFailure({ text, tempId }, e.message));
    }
};

const startConversation = async (
    next: Dispatch,
    action: ActionType<typeof postConversationStartRequest>,
    store: Store
) => {
    try {
        const state = store.getState();

        const config = getConversationsEmbedConfig();

        const response = await axios.post<BotStartResponse>(`${config.endpoint}/n/bot/start`, {
            botId: 4,
            contactInfo: {
                firstName: state.deal.person?.first_name,
                lastName: state.deal.person?.last_name,
                phone: state.deal.person?.mobile_phone,
                email: state.deal.person?.email,
            },
            dateOnSite: new Date().toISOString(),
            extraContext: {
                consumerData: {
                    consumerId: await getConsumerId(),
                    visitorId: await getId(),
                },
                initialUrl: window.location.href,
            },
            initialVisitorMessage: getSerializedInitialChat(state),
            locationId: config.locationId,
            senderId: uuidv4(),
        });

        next(postStartConversationSuccess(response.data));
    } catch (e) {
        next(postStartConversationFailure());
    }
};

const getTranscript = async (next: Dispatch, action: ActionType<typeof getTranscriptRequest>) => {
    const config = getConversationsEmbedConfig();

    try {
        const response = await axios.get<VisitorTranscriptResponse>(
            `${config.endpoint}/conversation/visitor/transcript?channelName=${action.payload.channel}`,
            {
                headers: {
                    "X-Visitor-Token": action.payload.token,
                },
            }
        );

        next(getTranscriptSuccess(response.data));
    } catch (e) {
        next(getTranscriptFailure());
    }
};

export const routes = {
    [postConversationMessageRequest.type]: postChat,
    [postConversationStartRequest.type]: startConversation,
    [getTranscriptRequest.type]: getTranscript,
};

export default (_next: Dispatch, action: any, store?: Store) => findRoute(routes)(_next, action, store);
